import {
  Box,
  Button as ChakraButton,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { DateFilter, Select, Text } from '@diamond/shared/ui';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';

const DATE_FILTER_CONFIG = {
  fromField: 'invoice_date-from' as const,
  toField: 'invoice_date-to' as const,
  buttonName: 'Pilih tanggal',
  shortYearFormat: true,
  buttonVariant: 'outline' as const,
};

interface LHCDetailFilterBarProps<T extends FieldValues = FieldValues> {
  methods: UseFormReturn<T>;
  handleDateFilter: (period: string) => { fromDate: string; toDate: string };
  updateDateParams: (fromDate: string, toDate: string) => void;
  plantValue: SelectOption | null;
  onPlantChange: (value: SelectOption | null) => void;
  plantOptions: SelectOption[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onAddSuratJalan: () => void;
}

export function LHCDetailFilterBar<T extends FieldValues = FieldValues>({
  methods,
  handleDateFilter,
  updateDateParams,
  plantValue,
  onPlantChange,
  plantOptions,
  searchValue,
  onSearchChange,
  onSearch,
  onAddSuratJalan,
}: LHCDetailFilterBarProps<T>) {
  return (
    <FormProvider {...methods}>
      <Flex
        gap={3}
        alignItems="flex-end"
        justifyContent="space-between"
        flexWrap="wrap"
        mb={4}
      >
        <Flex gap={3} alignItems="flex-end" flexWrap="wrap">
          {/* + Surat Jalan button */}
          <ChakraButton
            colorScheme="blue"
            bgColor="#0000A3"
            color="white"
            leftIcon={<AddIcon fontSize="small" />}
            size="md"
            onClick={onAddSuratJalan}
          >
            Surat Jalan
          </ChakraButton>

          {/* Tanggal Faktur */}
          <Box>
            <Text fontSize="xs" color="gray.600" mb={1}>
              Tanggal Faktur
            </Text>
            <DateFilter
              fromField={DATE_FILTER_CONFIG.fromField}
              toField={DATE_FILTER_CONFIG.toField}
              shortYearFormat={DATE_FILTER_CONFIG.shortYearFormat}
              buttonVariant={DATE_FILTER_CONFIG.buttonVariant}
              buttonName={DATE_FILTER_CONFIG.buttonName}
              onFilterChange={handleDateFilter}
              onCustomDateChange={updateDateParams}
            />
          </Box>

          {/* Plant dropdown */}
          <Box minW="150px">
            <Text fontSize="xs" color="gray.600" mb={1}>
              Plant
            </Text>
            <Select
              id="lhc-detail-plant-filter"
              placeholder="Semua"
              options={plantOptions}
              value={plantValue}
              onChange={(newValue) =>
                onPlantChange(newValue as SelectOption | null)
              }
            />
          </Box>
        </Flex>

        {/* Search */}
        <Flex gap={2} alignItems="flex-end">
          <InputGroup size="md" maxW="360px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon fontSize="small" style={{ color: '#A0AEC0' }} />
            </InputLeftElement>
            <Input
              placeholder="Cari Surat jalan, faktur, Bill to, No. Mutasi, Nama customer"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
          </InputGroup>
          <ChakraButton
            variant="outline"
            borderColor="#0000A3"
            color="#0000A3"
            _hover={{ bgColor: 'blue.50' }}
            size="md"
            onClick={onSearch}
          >
            Cari
          </ChakraButton>
        </Flex>
      </Flex>
    </FormProvider>
  );
}
